import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {capitalizeFirstLetter, getConfigByApp} from '@utils';
import {metaInfo} from '@configs';
import {HOME, MAJOR_CITIES, NON_CITY_LOCATION_IDS, SITEMAP} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {useDispatch} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {tagService} from '@jobseeker/common-core/services';
import {locations} from '@jobseeker/lotus/components/JobSearchBar/data';
import type {GetServerSidePropsContext} from 'next/types';
import {Box} from '@jobseeker/lotus/foundations';
import {Grid, Typography} from '@mui/material';
import {Link} from '@jobseeker/lotus/elements';
import {cleanUrl} from '@jobseeker/lotus/utils';

type SitemapProps = {
  seoConfig: object;
  metaData: object;
  city: string;
  cityPageUrls: {url: string; value: string}[];
  canonicalUrl: string;
};

const CityPage = ({
  seoConfig,
  metaData,
  city,
  cityPageUrls,
  canonicalUrl,
}: SitemapProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);
  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: 'Sitemap',
      item: `/sitemap`,
    },
    {
      name: `Jobs in ${capitalizeFirstLetter(city)}`,
      item: '',
    },
  ];

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout
        seoConfig={{
          ...seoConfig,
          canonical: `${
            (seoConfig as {canonical?: string}).canonical
          }${canonicalUrl.replace('/sitemap', '')}`,
        }}
      />
      <NavLayout>
        <ResponsiveLayout
          breadcrumbElements={breadcrumbElements}
          variant={VersionVariants.VERSION_TWO}
        >
          <Typography component="h2" mb={2}>
            Jobs in {city}
          </Typography>
          <Grid container xs={12}>
            {cityPageUrls.map((data, index) => {
              return (
                <Grid item key={index} xs={4}>
                  <Link href={data.url} target="_blank">
                    {data.value}
                  </Link>
                </Grid>
              );
            })}
          </Grid>
          <Box id="pagination-target" />
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const canonicalUrl = context?.resolvedUrl;
    const {slug = ''} = context.query;
    const city = String(slug).replace(/\.html/, '');

    const metaData = getConfigByApp(metaInfo, HOME, true, SITEMAP);
    const {
      header: {
        meta: {tabsList: categories},
      },
    } = metaData || {};

    const seoConfig = await loadSEO(SITEMAP);

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: SITEMAP},
    });

    const baseUrl = process.env.NEXT_PUBLIC_LOAD_CONFIG;

    const keywords: {tagName: string; tagSlug: string}[] =
      (await tagService.getTags('')) ?? [];

    const cityPageUrls: {url: string; value: string}[] = [];

    const cities =
      city === 'other-cities'
        ? locations.filter(
            location => !NON_CITY_LOCATION_IDS.includes(location.value)
          )
        : [{label: city, value: 0, selected: []}];

    cities?.forEach(location => {
      const {label: cityName} = location;
      const formattedCityName = cleanUrl(cityName);

      if (city === 'other-cities' && MAJOR_CITIES.includes(formattedCityName)) {
        return;
      }

      categories?.forEach((category: {label: string; tag: string}) => {
        const {label, tag} = category;
        const url = new URL(
          `${cleanUrl(tag)}-jobs-in-${formattedCityName}`,
          baseUrl
        ).href;

        cityPageUrls.push({
          url,
          value: `${label} > ${capitalizeFirstLetter(cityName)}`,
        });
      });

      keywords?.forEach(({tagName, tagSlug}) => {
        const url = new URL(`${tagSlug}-jobs-in-${formattedCityName}`, baseUrl)
          .href;

        cityPageUrls.push({
          url,
          value: `${tagName} > ${capitalizeFirstLetter(cityName)}`,
        });
      });
    });

    // Sort the list in alphabetical order
    cityPageUrls.sort((urlA, urlB) => {
      if (urlA.value.toUpperCase() < urlB.value.toUpperCase()) {
        return -1;
      }
      return 1;
    });

    return {
      props: {
        seoConfig,
        metaData,
        city,
        cityPageUrls,
        canonicalUrl,
      },
    };
  }
);

export default CityPage;
